/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { QueryClient, QueryCache, QueryClientProvider, useQuery } from 'react-query';
import LoadingSpinner from '@fff-web/fff-ui-shared/LoadingSpinner';
import { personalSurveyQuery } from './queries/personalSurveyQuery';
import { MappedAnswer, SurveyPageContext } from './components/context/SurveyPageContext';
import Container from './components/container/Container';
import reactQueryConfig from './queries/reactQueryConfig';
import { paginate } from './utils/paginator';
import { mapAnswers } from './utils/mapAnswers';
import { answersQuery } from './queries/answersQuery';
import withPersonalSurveyDisplayName from './HOC/withPersonalSurveyDisplayName';
import Question from './interfaces/question';

const queryCache = new QueryCache();
const queryClient = new QueryClient({ queryCache });

type SurveyProps = {
  lastButtonAction: () => void;
  firstBackButtonAction?: () => void;
  lastPage?: boolean;
};

const WrappedSurvey: React.FunctionComponent<SurveyProps> = ({
  lastButtonAction,
  firstBackButtonAction,
  lastPage,
}) => {
  const { data: survey, isLoading } = useQuery(
    personalSurveyQuery.key,
    personalSurveyQuery.query,
    reactQueryConfig
  );

  const [surveyPage, setSurveyPage] = useState(1);
  const [mappedAnswers, setMappedAnswers] = useState<Map<number, MappedAnswer>>(new Map());
  const [conditionalQuestionIds, setConditionalQuestionIds] = useState<Map<number, number[]>>(
    new Map()
  );
  const [paginatedSurvey, setPaginatedSurvey] = useState<Map<number, Question[]>>(new Map());

  const {
    data: answers,
    isIdle,
    isLoading: loadingAnswers,
  } = useQuery(answersQuery.key, () => survey && answersQuery.query(survey.id), {
    ...reactQueryConfig,
    enabled: !!survey?.id,
  });
  useEffect(() => {
    answers && setMappedAnswers(mapAnswers({ answers }));
  }, [answers]);

  useEffect(() => {
    const newPaginatedSurvey = survey ? paginate({ survey }) : new Map();
    setPaginatedSurvey(newPaginatedSurvey);
    if (lastPage) setSurveyPage(newPaginatedSurvey.size);
  }, [survey]);

  const increasePage = () => {
    setSurveyPage(surveyPage + 1);
    return null;
  };

  const decreasePage = () => {
    setSurveyPage(surveyPage - 1);
    return null;
  };

  const upsertMappedAnswers = (newMappedAnswers: Map<number, MappedAnswer>) => {
    setMappedAnswers(newMappedAnswers);
    return null;
  };

  const setConditionalQuestionId = (questionId: number, conditionalQuestionId: number[]) => {
    conditionalQuestionIds.set(questionId, conditionalQuestionId);
    setConditionalQuestionIds(new Map(conditionalQuestionIds));
  };

  const deleteConditionalQuestionId = (questionId: number) => {
    conditionalQuestionIds.delete(questionId);
    setConditionalQuestionIds(new Map(conditionalQuestionIds));
  };

  if (isLoading || isIdle || loadingAnswers) return <LoadingSpinner />;

  return (
    <SurveyPageContext.Provider
      value={{
        upsertMappedAnswers,
        mappedAnswers,
        paginatedSurvey,
        surveyPage,
        decreasePage,
        increasePage,
        conditionalQuestionIds,
        setConditionalQuestionId,
        deleteConditionalQuestionId,
        survey,
        lastButtonAction,
        firstBackButtonAction,
      }}
    >
      <Container />
    </SurveyPageContext.Provider>
  );
};

const Survey: React.FunctionComponent<SurveyProps> = (props) => {
  return (
    <QueryClientProvider client={queryClient}>
      <WrappedSurvey {...props} />
    </QueryClientProvider>
  );
};

Survey.displayName = 'PersonalSurvey';

const PersonalSurvey = withPersonalSurveyDisplayName(Survey);

export default PersonalSurvey;
